
import { useEffect, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ChevronRight, Bird, X } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2 } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";

interface VerseSuggestionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  verse: string;
  reference: string;
  explanation: string;
  prayer: string;
}

export function VerseSuggestionDialog({
  isOpen,
  onClose,
  verse,
  reference,
  explanation,
  prayer,
}: VerseSuggestionDialogProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [showContent, setShowContent] = useState(false);
  const [showDoveAnimation, setShowDoveAnimation] = useState(false);
  
  // Reset loading state when dialog opens
  useEffect(() => {
    if (isOpen) {
      setIsLoading(true);
      setShowContent(false);
      // Simulate loading time for the verse content
      const timer = setTimeout(() => {
        setIsLoading(false);
        // Small delay before showing content for animation
        setTimeout(() => setShowContent(true), 100);
      }, 1500);
      
      return () => clearTimeout(timer);
    }
  }, [isOpen]);
  
  // Ensure content is visible without zooming on mobile
  useEffect(() => {
    if (isOpen) {
      // Ensure proper viewport settings for mobile
      const viewportMeta = document.querySelector('meta[name="viewport"]');
      if (viewportMeta) {
        viewportMeta.setAttribute('content', 
          'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no');
      }
      
      // Reset scroll position
      window.scrollTo(0, 0);
    } else {
      // Reset viewport settings when dialog closes
      const viewportMeta = document.querySelector('meta[name="viewport"]');
      if (viewportMeta) {
        viewportMeta.setAttribute('content', 
          'width=device-width, initial-scale=1.0');
      }
    }
  }, [isOpen]);

  const backgroundStyle = {
    backgroundImage: `linear-gradient(rgba(10, 15, 30, 0.92), rgba(10, 15, 30, 0.92)), url(/lovable-uploads/1b5f601f-4627-4e8d-8fd8-0f2d55062ae8.png)`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  };

  // Function to clean text of asterisks, hash marks and leading numbers
  const cleanText = (text: string) => {
    return text
      .replace(/^\d+\.\s*/, '') // Remove leading numbers with dots
      .replace(/^\d+\s*/, '')   // Remove leading numbers without dots
      .replace(/[*#]/g, '');    // Remove asterisks and hash marks
  };

  // Handle the button click with animation
  const handleAmenClick = () => {
    setShowDoveAnimation(true);
    
    // Wait for animation to complete before closing
    setTimeout(() => {
      setShowDoveAnimation(false);
      onClose();
    }, 2000); // Increased duration to match the dove animation
  };

  // Decorative separator with gradient
  const DecorativeSeparator = () => (
    <div className="flex items-center justify-center py-4 sm:py-5">
      <div className="w-full max-w-xs flex items-center gap-2">
        <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/60 to-transparent"></div>
        <div className="h-1.5 w-1.5 rounded-full bg-white/70"></div>
        <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/60 to-transparent"></div>
      </div>
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="p-0 bg-transparent border-none w-[95vw] max-w-full overflow-hidden m-0">
        <div className="h-[95vh] w-full flex flex-col bg-verse-background" style={backgroundStyle}>
          <ScrollArea className="flex-1 overflow-y-auto w-full">
            <div className="relative px-4 sm:px-5 md:px-6 w-full pb-6">
              <button
                onClick={onClose}
                className="absolute top-3 right-3 text-white/80 hover:text-white/100 transition-colors z-10"
                aria-label="Close dialog"
              >
                <X className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>

              <div className="w-full mx-auto py-4 space-y-5 sm:space-y-6">
                <div className="w-full flex justify-center pt-2">
                  <img 
                    src="/lovable-uploads/97cec83c-b4fa-48c3-a12a-a678346efa1f.png" 
                    alt="Grace Logo" 
                    className="h-8 sm:h-10 md:h-12 drop-shadow-[0_0_15px_rgba(255,255,255,0.5)]" 
                  />
                </div>

                {isLoading ? (
                  <div className="flex flex-col items-center justify-center h-[50vh]">
                    <Loader2 className="h-10 w-10 sm:h-12 sm:w-12 text-white animate-spin mb-4" />
                    <p className="text-white/90 text-base sm:text-lg">Preparing your scripture...</p>
                  </div>
                ) : (
                  <div className={cn("space-y-6 transition-all duration-500", 
                    showContent ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4")}>
                    {/* Verse Section */}
                    <div className="space-y-3 bg-black/20 backdrop-blur-md rounded-lg p-4 sm:p-5 border border-white/10 shadow-xl">
                      <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-white mb-2 
                        after:content-[''] after:block after:w-16 after:h-0.5 after:bg-gradient-to-r after:from-blue-400 after:to-transparent after:mt-2">
                        Relevant Bible Verse
                      </h2>
                      <p className="text-xl sm:text-2xl md:text-3xl font-serif text-white leading-relaxed 
                        tracking-wide italic">
                        "{cleanText(verse)}"
                      </p>
                      <p className="text-base sm:text-lg md:text-xl text-white/80 font-serif pt-1">
                        - {cleanText(reference)}
                      </p>
                    </div>
                    
                    <DecorativeSeparator />
                    
                    {/* Understanding Section */}
                    <div className="bg-white/15 backdrop-blur-md rounded-lg p-4 sm:p-5 border border-white/15 shadow-xl">
                      <h3 className="text-base sm:text-lg font-semibold text-white mb-3 flex items-center">
                        <span className="w-1 h-5 bg-blue-400 mr-2 rounded-full"></span>
                        Understanding
                      </h3>
                      <p className="text-sm sm:text-base md:text-lg text-white leading-relaxed">
                        {cleanText(explanation)}
                      </p>
                    </div>
                    
                    <DecorativeSeparator />
                    
                    {/* Prayer Section */}
                    <div className="bg-white/10 backdrop-blur-md rounded-lg p-4 sm:p-5 border border-white/10 shadow-xl">
                      <h3 className="text-base sm:text-lg font-semibold text-white mb-3 flex items-center">
                        <span className="w-1 h-5 bg-blue-400 mr-2 rounded-full"></span>
                        Prayer
                      </h3>
                      <p className="text-white/90 text-sm sm:text-base md:text-lg leading-relaxed italic">
                        {cleanText(prayer)}
                      </p>
                    </div>

                    <div className="flex justify-center py-4 sm:py-6 relative">
                      {/* Bird animation */}
                      {showDoveAnimation && (
                        <div className="absolute top-0 left-0 right-0 bottom-0 flex items-center justify-center pointer-events-none overflow-visible">
                          <Bird 
                            className="text-white animate-dove-fly"
                            size={80}
                            strokeWidth={1.5}
                            fill="white"
                          />
                        </div>
                      )}
                      
                      <Button
                        onClick={handleAmenClick}
                        className={cn(
                          "bg-white/20 hover:bg-white/30 text-white gap-2 transition-all duration-300",
                          "px-6 py-5 rounded-full hover:scale-105 font-medium shadow-lg",
                          showDoveAnimation && "opacity-0 transition-opacity duration-300"
                        )}
                      >
                        Amen
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
  );
}
